import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, Loader2, Send, SquareCheckBig, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { ApiError } from '@/features/auth/api';
import { useIssueDraft } from '@/features/invoices/outbound/api';
import type { Finding } from './api';
import { previewDraftPdf } from './previewPdf';

/** Maps an issue-endpoint `problem+json` code to the toast copy key (falls back to a generic failure). */
function issueErrorKey(code: string | undefined): string {
  switch (code) {
    case 'NOT_READY_TO_ISSUE':
      return 'invoices.compose.issue.errors.not_ready';
    case 'DRAFT_NOT_ISSUABLE':
      return 'invoices.compose.issue.errors.not_issuable';
    case 'UNACKNOWLEDGED_WARNINGS':
      return 'invoices.compose.issue.errors.unacknowledged';
    case 'DUPLICATE_ISSUE':
      return 'invoices.compose.issue.errors.duplicate';
    default:
      return 'invoices.compose.issue.errors.failed';
  }
}

/**
 * The pre-send gate (plan D6): the explicit, deliberate confirmation before an invoice becomes a real
 * fiscal document in KSeF. Shows the number being assigned, a preview of the actual document (the same
 * server-rendered FA(3) → PDF the composer previews), and forces the user to acknowledge every rule
 * *warning* before the confirm unlocks (blockers are already impossible here — the button only appears
 * when the draft is issuable). Issuing flushes pending edits first (so the server evaluates the intended
 * content), then POSTs with a fresh idempotency id and routes to the new invoice's status page.
 */
export function IssueConfirmDialog({
  open,
  onOpenChange,
  draftId,
  invoiceNumber,
  warnings,
  flush,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId: number;
  invoiceNumber: string | null;
  warnings: Finding[];
  flush: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const navigate = useNavigate();
  const issue = useIssueDraft();

  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());
  const [outboundId, setOutboundId] = useState('');
  const [previewPending, setPreviewPending] = useState(false);

  // A fresh idempotency id + a clean acknowledgement slate each time the dialog opens; the id is reused
  // across in-dialog retries (a failed issue creates no row, so a replay is safe and correct).
  useEffect(() => {
    if (open) {
      setOutboundId(crypto.randomUUID());
      setAcknowledged(new Set());
    }
  }, [open]);

  const findingText = (f: Finding) => (lng.startsWith('pl') ? f.messagePl : f.messageEn);
  const toggle = (id: string) =>
    setAcknowledged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allAcknowledged = warnings.every((w) => acknowledged.has(w.id));
  const busy = issue.isPending;

  const onPreview = () => {
    setPreviewPending(true);
    previewDraftPdf({ id: draftId, watermark: t('invoices.compose.preview.watermark'), flush })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'DRAFT_NOT_RENDERABLE') {
          toast.error(t('invoices.compose.preview.not_renderable'));
        } else {
          toast.error(t('invoices.compose.preview.failed'));
        }
      })
      .finally(() => setPreviewPending(false));
  };

  const onConfirm = async () => {
    try {
      // Persist the current editor content first: the server issues the *saved* draft, so a pending
      // autosave must land before we ask it to freeze the document.
      await flush();
      const res = await issue.mutateAsync({
        draftId,
        body: { outboundId, acknowledgedWarnings: [...acknowledged] },
      });
      onOpenChange(false);
      toast.success(t('invoices.compose.issue.success', { number: invoiceNumber ?? '' }));
      navigate(`/invoices/sent/${res.outboundId}`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      toast.error(t(issueErrorKey(code)));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (busy ? undefined : onOpenChange(next))}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('invoices.compose.issue.title')}</DialogTitle>
          <DialogDescription>
            {t('invoices.compose.issue.description', {
              number: invoiceNumber ?? t('invoices.compose.issue.number_pending'),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={previewPending}
            onClick={onPreview}
          >
            {previewPending ? (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            ) : (
              <FileText data-icon="inline-start" />
            )}
            {t('invoices.compose.issue.preview')}
          </Button>

          {warnings.length > 0 ? (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="size-4" />
                {t('invoices.compose.issue.acknowledge_warnings')}
              </p>
              <ul className="max-h-56 space-y-1.5 overflow-y-auto">
                {warnings.map((warning) => {
                  const checked = acknowledged.has(warning.id);
                  return (
                    <li key={warning.id}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => toggle(warning.id)}
                        className={cn(
                          'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm outline-none transition-colors',
                          'focus-visible:ring-2 focus-visible:ring-ring/60',
                          checked
                            ? 'border-primary/40 bg-accent/50'
                            : 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10',
                        )}
                      >
                        {checked ? (
                          <SquareCheckBig className="mt-0.5 size-4 shrink-0 text-primary" />
                        ) : (
                          <Square className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                        )}
                        <span>{findingText(warning)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!allAcknowledged || busy}
            onClick={onConfirm}
          >
            {busy ? <Loader2 data-icon="inline-start" className="animate-spin" /> : <Send data-icon="inline-start" />}
            {t('invoices.compose.issue.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
